import type { SlotName } from "zudoku";

/**
 * Injection target types. Each target maps to a path segment under
 * the configured injections directory (`{target}/…`) and owns a set of
 * Zudoku slots (see PredefinedSlotNames in zudoku).
 *
 * Path: `{target}/{id}/{slot-name}.mdx`
 */
export type InjectionTarget =
  | "tag"
  | "footer"
  | "head-navigation"
  | "layout"
  | "top-navigation"
  | "navigation"
  | "mobile-top-bar"
  | "api-keys";

export type TargetDefinition = {
  /** Zudoku slot names allowed for this target. */
  validSlots: ReadonlySet<SlotName>;
  /**
   * Resolve the injection id from the current URL pathname.
   * Returns undefined when this target does not apply to the page.
   */
  resolveId: (pathname: string, basePath?: string) => string | undefined;
};

/** Site-wide targets use this id (e.g. injections/footer/default/footer-before.mdx). */
export const DEFAULT_INJECTION_ID = "default";

function alwaysDefault(_pathname: string, _basePath?: string): string {
  return DEFAULT_INJECTION_ID;
}

function stripBasePath(pathname: string, basePath = ""): string {
  const base = basePath.replace(/\/$/, "");
  if (base && pathname.startsWith(base)) {
    return pathname.slice(base.length) || "/";
  }
  return pathname;
}

/** OpenAPI tag pages: /1.3/{tag-slug} → id = tag slug (e.g. "tag"). */
function tagSlugFromPathname(pathname: string, basePath = ""): string | undefined {
  const path = stripBasePath(pathname, basePath);
  const match = path.match(/^\/1\.3\/([^/]+)/);
  if (!match) return undefined;
  const slug = match[1];
  if (!slug || slug.startsWith("~")) return undefined;
  return slug;
}

/** API keys settings pages only. */
function apiKeysIdFromPathname(pathname: string, basePath = ""): string | undefined {
  const path = stripBasePath(pathname, basePath);
  if (!path.includes("api-keys")) return undefined;
  return DEFAULT_INJECTION_ID;
}

export const TARGETS: Record<InjectionTarget, TargetDefinition> = {
  /**
   * OpenAPI tag pages (OperationList).
   * Example: injections/tag/tag/content-before.mdx → /1.3/tag
   */
  tag: {
    validSlots: new Set<SlotName>(["content-before", "content-after"]),
    resolveId: tagSlugFromPathname,
  },

  /** Site footer. */
  footer: {
    validSlots: new Set<SlotName>(["footer-before", "footer-after"]),
    resolveId: alwaysDefault,
  },

  /** Header nav actions (start/end of head navigation). */
  "head-navigation": {
    validSlots: new Set<SlotName>([
      "head-navigation-start",
      "head-navigation-end",
    ]),
    resolveId: alwaysDefault,
  },

  /** Around the site header in the layout shell. */
  layout: {
    validSlots: new Set<SlotName>([
      "layout-before-head",
      "layout-after-head",
    ]),
    resolveId: alwaysDefault,
  },

  /** Top navigation bar (below header). */
  "top-navigation": {
    validSlots: new Set<SlotName>([
      "top-navigation-before",
      "top-navigation-after",
      "top-navigation-side",
    ]),
    resolveId: alwaysDefault,
  },

  /** Sidebar navigation. */
  navigation: {
    validSlots: new Set<SlotName>(["navigation-before", "navigation-after"]),
    resolveId: alwaysDefault,
  },

  /** Mobile top bar (end). */
  "mobile-top-bar": {
    validSlots: new Set<SlotName>(["mobile-top-bar-end"]),
    resolveId: alwaysDefault,
  },

  /** API keys list page. */
  "api-keys": {
    validSlots: new Set<SlotName>([
      "api-keys-list-page",
      "api-keys-list-page-before-keys",
    ]),
    resolveId: apiKeysIdFromPathname,
  },
};

export function isInjectionTarget(name: string): name is InjectionTarget {
  return Object.hasOwn(TARGETS, name);
}
